import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TablePagination,
  Button,
} from '@mui/material';
import { ArrowBack, Search } from '@mui/icons-material';
import { ProductVm } from 'api/catalog/product.vm';
import { useNavigate, useParams } from 'react-router-dom';
import { getProduct } from 'api/catalog/get-product';
import { getProductBatches } from 'api/catalog/get-product-batches';
import { getProductSerialNumbers } from 'api/catalog/get-product-serial-numbers';
import { CallbackObject } from 'api/core/get-page-function';
import { BatchVm, BatchStatus } from 'api/catalog/batch.vm';
import { SerialNumberVm, SerialNumberStatus } from 'api/catalog/serial-number.vm';
import { GetProductBatchesVm } from 'api/catalog/get-product-batches.vm';
import { GetProductSerialNumbersVm } from 'api/catalog/get-product-serial-numbers.vm';

const ProductPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const [product, setProduct] = useState<ProductVm | null>(null);
  const [loading, setLoading] = useState(false);
  const [isBatchSerialLoading, setIsBatchSerialLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<BatchVm[] | SerialNumberVm[]>([]);
  const [nextOffset, setNextOffset] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const fetchProductDetails = () => {
    if (!productId) return;
    setLoading(true);
    getProduct(productId, {
      success: (response: ProductVm) => {
        setProduct(response);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao carregar detalhes do produto:', err);
        setLoading(false);
      },
    });
  };

  const fetchItems = (resetPage = false) => {
    if (!productId || !product) return;

    setIsBatchSerialLoading(true);

    const callback: CallbackObject<GetProductBatchesVm> | CallbackObject<GetProductSerialNumbersVm> = {
      success: (response) => {
        setItems(response.objects);
        setTotalCount(response.totalItems);
        setNextOffset(response.nextOffset);
        if (resetPage) setPage(0);
        setIsBatchSerialLoading(false);
      },
      error: (err) => {
        console.error('Falha ao carregar itens:', err);
        setIsBatchSerialLoading(false);
      },
    };

    const term = searchTerm.trim() || null;

    if (product.usesBatch) {
      getProductBatches(productId, term, nextOffset, rowsPerPage, callback as any);
    } else if (product.usesSerialNumber) {
      getProductSerialNumbers(productId, term, nextOffset, rowsPerPage, callback as any);
    }
  };

  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  useEffect(() => {
    if (product) {
      fetchItems(true);
    }
  }, [product, rowsPerPage]);

  const handleSearch = () => {
    fetchItems(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchItems();
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const navigateToTraceability = (item: BatchVm | SerialNumberVm) => {
    navigate(`/products/products/${productId}/trace/${item.barcode}`);
  };

  const getBooleanChip = (value: boolean) => <Chip label={value ? 'Sim' : 'Não'} color={value ? 'success' : 'error'} size="small" />;

  const getStatusChip = (status: BatchStatus | SerialNumberStatus) => {
    let color: 'success' | 'error' | 'warning' | 'default' = 'default';

    if (status === BatchStatus.ACTIVE || status === SerialNumberStatus.ACTIVE) {
      color = 'success';
    } else if (status === BatchStatus.INACTIVE || status === SerialNumberStatus.INACTIVE) {
      color = 'error';
    } else if (status === BatchStatus.UNINITIALIZED || status === SerialNumberStatus.UNINITIALIZED) {
      color = 'warning';
    }

    let label = '';
    switch (status) {
      case BatchStatus.ACTIVE:
      case SerialNumberStatus.ACTIVE:
        label = 'Ativo';
        break;
      case BatchStatus.INACTIVE:
      case SerialNumberStatus.INACTIVE:
        label = 'Inativo';
        break;
      case BatchStatus.UNINITIALIZED:
      case SerialNumberStatus.UNINITIALIZED:
        label = 'Não Inicializado';
        break;
      default:
        label = status;
    }

    return <Chip label={label} color={color} size="small" />;
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-PT');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" onClick={handleBackNavigation} sx={{ cursor: 'pointer' }}>
            Produtos
          </Link>
          <Typography color="text.primary">{product?.name || 'Detalhes do Produto'}</Typography>
        </Breadcrumbs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : product ? (
        <>
          {/* Secção de Informação do Produto */}
          <Paper sx={{ mb: 3, p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              Informação
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  SKU
                </Typography>
                <Typography variant="body1">{product.sku}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  ID Externo
                </Typography>
                <Typography variant="body1">{product.externalId || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Código Opcional
                </Typography>
                <Typography variant="body1">{product.optionalCode || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Nome
                </Typography>
                <Typography variant="body1">{product.name}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Quantidade por Embalagem
                </Typography>
                <Typography variant="body1">
                  {product.packageSize} {product.measureUnitAcronym}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Unidade de Medida
                </Typography>
                <Typography variant="body1">{product.measureUnitAcronym}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Fornecedor Padrão
                </Typography>
                <Typography variant="body1">{product.defaultSupplierId || '-'}</Typography>
              </Box>
            </Box>
            {product.description && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Descrição
                </Typography>
                <Typography variant="body1">{product.description}</Typography>
              </Box>
            )}
          </Paper>

          {/* Secção de Definições do Produto */}
          <Paper sx={{ mb: 3, p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              Definições
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: 3,
              }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Usa Lote
                </Typography>
                {getBooleanChip(product.usesBatch)}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Usa Número de Série
                </Typography>
                {getBooleanChip(product.usesSerialNumber)}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Precisa Preparação
                </Typography>
                {getBooleanChip(product.needsPreparation)}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Usa Data de Validade
                </Typography>
                {getBooleanChip(product.usesExpiryDate)}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Usa Contentor
                </Typography>
                {getBooleanChip(product.usesContainer)}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  É Contentor
                </Typography>
                {getBooleanChip(product.isContainer)}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Usa Embalagem
                </Typography>
                {getBooleanChip(product.usesPackage)}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Usa Quantidade Inteira
                </Typography>
                {getBooleanChip(product.usesIntegerQuantity)}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Usa Unidades Atómicas
                </Typography>
                {getBooleanChip(product.usesAtomicUnits)}
              </Box>
            </Box>
          </Paper>

          {product.usesBatch || product.usesSerialNumber ? (
            <Paper sx={{ mb: 3, p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                {product.usesBatch ? 'Lotes' : 'Números de Série'}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={`Procurar ${product.usesBatch ? 'lotes' : 'números de série'}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  sx={{ mr: 2 }}
                />
                <Button variant="contained" startIcon={<Search />} onClick={handleSearch} size="large">
                  Procurar
                </Button>
              </Box>

              {isBatchSerialLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Lote/Número de Série</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Data de Validade</TableCell>
                          <TableCell align="right">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.length > 0 ? (
                          items.map((item) => (
                            <TableRow key={item.barcode}>
                              <TableCell>{item.barcode}</TableCell>
                              <TableCell>
                                {getStatusChip(product.usesBatch ? (item as BatchVm).status : (item as SerialNumberVm).status)}
                              </TableCell>
                              <TableCell>{formatDate(item.expiration)}</TableCell>
                              <TableCell align="right">
                                <Button variant="outlined" onClick={() => navigateToTraceability(item)} size="small">
                                  Ver Rastreabilidade
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="text.secondary">
                                Nenhum {product.usesBatch ? 'lote' : 'número de série'} encontrado
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {items.length > 0 && (
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                </>
              )}
            </Paper>
          ) : (
            <Paper sx={{ mb: 3, p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                Rastreabilidade
              </Typography>
              <Typography variant="body1">Este produto não utiliza lotes nem números de série.</Typography>
            </Paper>
          )}
        </>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="error">
            Falha ao carregar detalhes do produto
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ProductPage;
